/**
 * PATH: packages/bills/src/components/ReclassifyMenu.tsx
 * FILE_ID: BILLS-PKG-CMP-RECLASSIFY-010
 * PURPOSE: The header's "Not a bill?" dropdown — reclassify the bill's email
 *          out of the AP queue (it's our outgoing invoice / a remittance /
 *          spam).
 *
 * WHY: Self-contained open/close + click-outside/Escape behaviour lived inline
 *      in BillDataPanel; pulling it out (with its own state/ref/effect) drops a
 *      chunk of orchestration from the panel while preserving behaviour exactly.
 *      The caller still gates rendering on ``status !== 'pushed'`` (see
 *      BillListView._cleanup_orphaned_pending — pushed rows aren't swept even
 *      if the email category later changes, so reclassifying then misleads).
 *
 * Extracted from BillDataPanel.tsx (P4-1 split) — no behaviour change.
 */

import { useRef, useState } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { Button, useCloseOnOutsidePointer } from '@quotech/ui';
import { RECLASSIFY_OPTIONS } from '../reclassify';

export function ReclassifyMenu({
  busy,
  onReclassify,
  className = '',
}: {
  busy: boolean;
  onReclassify: (category: string) => Promise<void>;
  /** Layout classes for the wrapper (e.g. flex-1 to stretch in a toolbar row). */
  className?: string;
}) {
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const reclassifyMenuRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close for the reclassify dropdown so it
  // behaves like every other dropdown menu in the dashboard. Bound
  // only while open so we're not paying for global listeners.
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(reclassifyOpen, () => setReclassifyOpen(false), [reclassifyMenuRef]);

  return (
    <div ref={reclassifyMenuRef} className={'relative ' + className}>
      {/* Sized to match the header action bar (h-8 / text-xs / rounded-md) so
          the row reads as ONE toolbar instead of mismatched button heights. */}
      <Button
        variant="outline"
        size="sm"
        className="h-8 w-full gap-1.5 rounded-md px-2.5 text-xs text-muted-foreground hover:text-foreground"
        disabled={busy}
        onClick={() => setReclassifyOpen((v) => !v)}
        title="This isn't a bill — reclassify the email out of the AP queue"
        aria-haspopup="menu"
        aria-expanded={reclassifyOpen}
      >
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        Not a bill
        <ChevronDown className="h-3 w-3 opacity-70" aria-hidden />
      </Button>
      {reclassifyOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-md border border-border bg-popover shadow-lg"
        >
          <ul className="max-h-80 overflow-y-auto py-1 text-xs">
            {RECLASSIFY_OPTIONS.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  disabled={busy}
                  role="menuitem"
                  onClick={async () => {
                    setReclassifyOpen(false);
                    await onReclassify(opt.value);
                  }}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {opt.hint}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
